"use client";
import React, { useRef, useState } from "react";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";
import { businesses } from "@/lib/data";

const featured = businesses.filter((b) => b.isSponsored || b.isFeatured).slice(0, 8);

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Icon
          key={star}
          name="StarIcon"
          size={12}
          variant={star <= Math.round(rating) ? "solid" : "outline"}
          className={star <= Math.round(rating) ? "text-accent" : "text-muted-foreground"}
        />
      ))}
    </div>
  );
}

export default function FeaturedBusinesses() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [favorites, setFavorites] = useState<string[]>([]);

  const toggleFav = (id: string) => {
    setFavorites((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]
    );
  };

  const scroll = (dir: "left" | "right") => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir === "left" ? -320 : 320, behavior: "smooth" });
    }
  };

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="text-section-title text-foreground">
              Konya&apos;nın Seçkin İşletmeleri
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => scroll("left")}
              className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary transition-all"
              aria-label="Önceki"
            >
              <Icon name="ChevronLeftIcon" size={18} />
            </button>
            <button
              onClick={() => scroll("right")}
              className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary transition-all"
              aria-label="Sonraki"
            >
              <Icon name="ChevronRightIcon" size={18} />
            </button>
          </div>
        </div>

        {/* Carousel */}
        <div
          ref={scrollRef}
          className="flex gap-5 overflow-x-auto scrollbar-hide pb-2"
        >
          {featured.map((biz) => (
            <div
              key={biz.id}
              className="business-card min-w-[280px] max-w-[280px] flex-shrink-0"
            >
              {/* Cover Image */}
              <div className="relative h-44 overflow-hidden">
                <AppImage
                  src={biz.coverImage}
                  alt={`${biz.name} — ${biz.category}, ${biz.district}, Konya`}
                  fill
                  className="object-cover group-hover:scale-105 transition-transform duration-500"
                  sizes="280px"
                />
                {/* Badges */}
                <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                  {biz.isSponsored && (
                    <span className="badge-featured">
                      <Icon name="SparklesIcon" size={10} />
                      Öne Çıkan
                    </span>
                  )}
                </div>
                {/* Favorite */}
                <button
                  onClick={() => toggleFav(biz.id)}
                  aria-label={favorites.includes(biz.id) ? "Favoriden Çıkar" : "Favoriye Ekle"}
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center transition-all hover:scale-110"
                >
                  <Icon
                    name="HeartIcon"
                    size={16}
                    variant={favorites.includes(biz.id) ? "solid" : "outline"}
                    className={favorites.includes(biz.id) ? "text-primary" : "text-muted-foreground"}
                  />
                </button>
                {/* Status */}
                <div className="absolute bottom-3 left-3">
                  <span className={biz.isOpen ? "badge-open" : "badge-closed"}>
                    <span className={`w-1.5 h-1.5 rounded-full ${biz.isOpen ? "bg-green-500" : "bg-red-500"}`} />
                    {biz.isOpen ? "Açık" : "Kapalı"}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <h3 className="font-bold text-base text-foreground leading-tight">{biz.name}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {biz.category} · {biz.district}
                    </p>
                  </div>
                  <span className="text-xs font-semibold text-muted-foreground shrink-0">{biz.priceRange}</span>
                </div>

                <StarRating rating={biz.rating} />
                <p className="text-xs text-muted-foreground mt-0.5">{biz.reviewCount} değerlendirme</p>

                {/* Quick Actions */}
                <div className="flex gap-2 mt-4">
                  <a
                    href={`tel:${biz.phone}`}
                    aria-label="Ara"
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold border border-border text-foreground hover:border-secondary hover:text-secondary transition-all"
                  >
                    <Icon name="PhoneIcon" size={14} />
                    Ara
                  </a>
                  <a
                    href={`https://wa.me/${biz.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="WhatsApp"
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold border border-border text-foreground hover:border-green-500 hover:text-green-500 transition-all"
                  >
                    <Icon name="ChatBubbleLeftRightIcon" size={14} />
                    WhatsApp
                  </a>
                  <Link
                    href="/business-detail"
                    aria-label="Detay"
                    className="flex items-center justify-center w-9 h-9 rounded-xl border border-border text-muted-foreground hover:border-primary hover:text-primary transition-all"
                  >
                    <Icon name="ArrowRightIcon" size={14} />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-8">
          <Link href="/directory" className="btn-outline">
            Tüm Firmaları Gör
            <Icon name="ArrowRightIcon" size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}